"use client";
import * as React from "react";
import styled from "styled-components";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({ 
  currentPage, 
  totalPages,
  onPageChange
}) => {
  // Generate page numbers array with consideration for ellipsis
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      if (currentPage > 3) {
        // Add ellipsis if current page is away from start
        pageNumbers.push(-1); // -1 represents ellipsis
      }
      
      // Calculate range around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        // Add ellipsis if current page is away from end
        pageNumbers.push(-2); // -2 represents ellipsis
      }
      
      // Always show last page
      if (totalPages > 1) {
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };
  
  const pageNumbers = getPageNumbers();
  
  return (
    <PaginationContainer>
      <PaginationButton 
        first 
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <PaginationIcon
          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/3ab615182261f73f054b837c6ed3a8f38c1754ac?placeholderIfAbsent=true"
          alt="Previous"
        />
      </PaginationButton>
      
      {pageNumbers.map((pageNumber, index) => {
        if (pageNumber === -1 || pageNumber === -2) {
          // Render ellipsis
          return <PageButton key={`ellipsis-${index}`}>...</PageButton>;
        }
        
        return (
          <PageButton 
            key={pageNumber}
            active={pageNumber === currentPage}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber}
          </PageButton>
        );
      })}
      
      <PaginationButton 
        last 
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <PaginationIcon
          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/ef2384b863e51354bc89285c83bfbc18daacca08?placeholderIfAbsent=true"
          alt="Next"
        />
      </PaginationButton>
    </PaginationContainer>
  );
};

const PaginationContainer = styled.nav`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: -1px;
`;

const PaginationButton = styled.button<{ first?: boolean; last?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 36px;
  border: 1px solid #d8e0f0;
  background-color: #fcfcfc;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  border-radius: ${(props) =>
    props.first ? "6px 0 0 6px" : props.last ? "0 6px 6px 0" : "0"};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#fcfcfc" : "#f4f4f4")};
  }
`;

const PageButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 36px;
  border: 1px solid #d8e0f0;
  background-color: ${(props) => (props.active ? "#1777cf" : "#fcfcfc")};
  color: ${(props) => (props.active ? "#fcfcfc" : "#3a3f51")};
  font-family:
    Inter,
    -apple-system,
    Roboto,
    Helvetica,
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.active ? "#1777cf" : "#f4f4f4")};
  }
`;

const PaginationIcon = styled.img`
  width: 6px;
  aspect-ratio: 5/3;
  object-fit: contain;
`;
